/**
 * ScenariosView Component
 *
 * Full EDGS scenario browser with comparison and deep-dive analytics.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../services/api';
import DataBadge from './DataBadge';
import DataProvenanceRow from './DataProvenanceRow';

interface ScenarioListItem {
  name: string;
  description: string | null;
  variant: string;
  planning_horizon: number;
  scenario_id: string;
}

interface ScenarioListResponse {
  scenarios: ScenarioListItem[];
  total: number;
  variants: string[];
  horizons: number[];
}

interface DemandBreakdown {
  base_demand_mw: number;
  scaled_base_mw: number;
  ev_demand_mw: number;
  h2_demand_mw: number;
  dc_demand_mw: number;
  process_heat_mw: number;
  total_demand_mw: number;
  demand_increase_pct: number;
}

interface RegionalLoad {
  region: string;
  total_base_mw: number;
  total_modified_mw: number;
  dc_load_mw: number;
  ev_load_mw: number;
  h2_load_mw: number;
  modification_factor: number;
}

interface ScenarioAnalysis {
  scenario: any;
  demand_breakdown: DemandBreakdown;
  regional_loads: RegionalLoad[];
  metrics: Record<string, number>;
}

interface StrategyListItem {
  name: string;
  description: string | null;
}

interface ScenarioSimulationResponse {
  scenario: string;
  strategy: string | null;
  task_id: string;
  status: string;
  config: Record<string, any>;
}

const VARIANT_COLORS: Record<string, string> = {
  BU: 'bg-gray-600',
  AP: 'bg-blue-600',
  NZ: 'bg-green-600',
  DF: 'bg-orange-600',
  AB: 'bg-yellow-600',
  AI: 'bg-pink-600',
  CUSTOM: 'bg-gray-500',
};

const REGION_LABELS: Record<string, string> = {
  UNI: 'Upper North Island',
  LNI: 'Lower North Island',
  USI: 'Upper South Island',
  LSI: 'Lower South Island',
};

const parseResponseJson = async <T,>(
  response: Response,
  fallback: string
): Promise<T> => {
  const contentType = response.headers.get('content-type') || '';
  const bodyText = await response.text();

  if (!response.ok) {
    if (contentType.includes('application/json') || bodyText.trim().startsWith('{')) {
      try {
        const data = JSON.parse(bodyText);
        throw new Error(data?.detail || data?.message || fallback);
      } catch {
        throw new Error(fallback);
      }
    }
    throw new Error(bodyText || fallback);
  }

  if (!bodyText) {
    throw new Error(fallback);
  }

  if (contentType.includes('application/json') || bodyText.trim().startsWith('{') || bodyText.trim().startsWith('[')) {
    return JSON.parse(bodyText) as T;
  }

  throw new Error(bodyText || fallback);
};

const MetricTile: React.FC<{
  label: string;
  value: string;
  tone?: 'neutral' | 'good' | 'warning';
}> = ({ label, value, tone = 'neutral' }) => {
  const toneClasses = {
    neutral: 'text-white',
    good: 'text-green-300',
    warning: 'text-orange-300',
  };
  return (
    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-semibold ${toneClasses[tone]}`}>{value}</div>
    </div>
  );
};

const ScenariosView: React.FC = () => {
  const [scenarios, setScenarios] = useState<ScenarioListItem[]>([]);
  const [variants, setVariants] = useState<string[]>([]);
  const [horizons, setHorizons] = useState<number[]>([]);
  const [variantFilter, setVariantFilter] = useState<string>('');
  const [horizonFilter, setHorizonFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<ScenarioAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listFetchedAt, setListFetchedAt] = useState<Date | null>(null);
  const [analysisFetchedAt, setAnalysisFetchedAt] = useState<Date | null>(null);
  const [simulationQueuedAt, setSimulationQueuedAt] = useState<Date | null>(null);
  const [baseDemandMw, setBaseDemandMw] = useState(5500);
  const [baseDcMw, setBaseDcMw] = useState(104);
  const [simSteps, setSimSteps] = useState(24);
  const [simTimestepMinutes, setSimTimestepMinutes] = useState(60);
  const [simLoadFactor, setSimLoadFactor] = useState(0.7);
  const [simUseStorage, setSimUseStorage] = useState(true);
  const [simUseFlexLoads, setSimUseFlexLoads] = useState(true);
  const [pinned, setPinned] = useState<string[]>([]);
  const [pinnedAnalyses, setPinnedAnalyses] = useState<Record<string, ScenarioAnalysis>>({});
  const [compareError, setCompareError] = useState<string | null>(null);
  const [strategies, setStrategies] = useState<StrategyListItem[]>([]);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategyError, setStrategyError] = useState<string | null>(null);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [simulation, setSimulation] = useState<ScenarioSimulationResponse | null>(null);
  const [simulationError, setSimulationError] = useState<string | null>(null);
  const [simulationLoading, setSimulationLoading] = useState(false);

  const fetchScenarioList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams();
      if (variantFilter) params.append('variant', variantFilter);
      if (horizonFilter) params.append('horizon', horizonFilter);
      const query = params.toString();
      const url = query ? `/scenarios?${query}` : '/scenarios';
      const response = await apiFetch(url);
      const data = await parseResponseJson<ScenarioListResponse>(
        response,
        'Failed to load scenarios'
      );
      setScenarios(data.scenarios);
      setVariants(data.variants);
      setHorizons(data.horizons);
      setListFetchedAt(new Date());
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load scenarios');
    } finally {
      setListLoading(false);
    }
  }, [variantFilter, horizonFilter]);

  useEffect(() => {
    fetchScenarioList();
  }, [fetchScenarioList]);

  const fetchStrategies = useCallback(async () => {
    setStrategyLoading(true);
    setStrategyError(null);
    try {
      const response = await apiFetch('/strategies');
      const data = await parseResponseJson<StrategyListItem[]>(response, 'Failed to load strategies');
      setStrategies(data);
    } catch (e) {
      setStrategyError(e instanceof Error ? e.message : 'Failed to load strategies');
    } finally {
      setStrategyLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  const fetchScenarioAnalysis = useCallback(async (name: string) => {
    const params = new URLSearchParams();
    params.append('base_demand_mw', baseDemandMw.toString());
    params.append('base_dc_mw', baseDcMw.toString());
    const response = await apiFetch(`/scenarios/${name}/analysis?${params}`);
    return parseResponseJson<ScenarioAnalysis>(response, 'Failed to load scenario analysis');
  }, [baseDemandMw, baseDcMw]);

  const loadSelectedScenario = useCallback(async () => {
    if (!selectedScenario) {
      setAnalysis(null);
      return;
    }
    setAnalysisLoading(true);
    setAnalysisError(null);
    try {
      const data = await fetchScenarioAnalysis(selectedScenario);
      setAnalysis(data);
      setAnalysisFetchedAt(new Date());
    } catch (e) {
      setAnalysisError(e instanceof Error ? e.message : 'Failed to load scenario analysis');
      setAnalysis(null);
    } finally {
      setAnalysisLoading(false);
    }
  }, [fetchScenarioAnalysis, selectedScenario]);

  useEffect(() => {
    loadSelectedScenario();
  }, [loadSelectedScenario]);

  useEffect(() => {
    setSimulation(null);
    setSimulationError(null);
    setSimulationQueuedAt(null);
  }, [selectedScenario]);

  useEffect(() => {
    if (pinned.length === 0) {
      setPinnedAnalyses({});
      return;
    }
    let isActive = true;
    const loadPinned = async () => {
      try {
        const results = await Promise.all(
          pinned.map(async (name) => {
            const data = await fetchScenarioAnalysis(name);
            return [name, data] as const;
          })
        );
        if (!isActive) return;
        const next = results.reduce<Record<string, ScenarioAnalysis>>((acc, [name, data]) => {
          acc[name] = data;
          return acc;
        }, {});
        setPinnedAnalyses(next);
      } catch {
        if (isActive) {
          setCompareError('Failed to refresh pinned scenarios.');
        }
      }
    };
    loadPinned();
    return () => {
      isActive = false;
    };
  }, [pinned, fetchScenarioAnalysis]);

  const filteredScenarios = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return scenarios;
    return scenarios.filter((scenario) =>
      scenario.name.toLowerCase().includes(query)
      || scenario.description?.toLowerCase().includes(query)
      || scenario.variant.toLowerCase().includes(query)
    );
  }, [scenarios, search]);

  const togglePin = (name: string) => {
    setCompareError(null);
    setPinned((current) => {
      if (current.includes(name)) {
        return current.filter((item) => item !== name);
      }
      if (current.length >= 3) {
        setCompareError('You can compare up to three scenarios at once.');
        return current;
      }
      return [...current, name];
    });
  };

  const derived = useMemo(() => {
    if (!analysis) return null;
    const mix = analysis.scenario?.generation_mix || {};
    const totalCapacityMw = (
      (mix.wind_capacity_gw || 0)
      + (mix.solar_capacity_gw || 0)
      + (mix.hydro_capacity_gw || 0)
      + (mix.geothermal_capacity_gw || 0)
      + (mix.gas_capacity_gw || 0)
    ) * 1000;
    const totalCapacityGw = totalCapacityMw / 1000;
    const renewableCapacityMw = (
      (mix.wind_capacity_gw || 0)
      + (mix.solar_capacity_gw || 0)
      + (mix.hydro_capacity_gw || 0)
      + (mix.geothermal_capacity_gw || 0)
    ) * 1000;
    const thermalCapacityMw = (mix.gas_capacity_gw || 0) * 1000;
    const demandTotal = analysis.demand_breakdown.total_demand_mw;
    const headroomMw = totalCapacityMw - demandTotal;
    const headroomPct = totalCapacityMw > 0 ? (headroomMw / totalCapacityMw) * 100 : 0;
    const flexibility = analysis.scenario?.flexibility || {};
    const dcFlexMw = analysis.demand_breakdown.dc_demand_mw * (flexibility.dc_flexibility_share || 0);
    const h2FlexMw = analysis.demand_breakdown.h2_demand_mw * (flexibility.h2_electrolyzer_flexibility || 0);
    const demandResponseMw = flexibility.demand_response_potential_mw || 0;
    const totalFlexMw = dcFlexMw + h2FlexMw + demandResponseMw;
    const batteryHours = flexibility.battery_power_gw
      ? flexibility.battery_storage_gwh / flexibility.battery_power_gw
      : 0;
    return {
      totalCapacityMw,
      totalCapacityGw,
      renewableCapacityMw,
      thermalCapacityMw,
      headroomMw,
      headroomPct,
      totalFlexMw,
      batteryHours,
    };
  }, [analysis]);

  const selectedStrategyMeta = useMemo(() => {
    if (!selectedStrategy) return null;
    return strategies.find((strategy) => strategy.name === selectedStrategy) || null;
  }, [selectedStrategy, strategies]);

  const comparisonRows = useMemo(() => {
    return pinned
      .map((name) => pinnedAnalyses[name])
      .filter(Boolean)
      .map((data) => {
        const mix = data.scenario?.generation_mix || {};
        const totalCapacityMw = (
          (mix.wind_capacity_gw || 0)
          + (mix.solar_capacity_gw || 0)
          + (mix.hydro_capacity_gw || 0)
          + (mix.geothermal_capacity_gw || 0)
          + (mix.gas_capacity_gw || 0)
        ) * 1000;
        return {
          name: data.scenario?.name || 'scenario',
          horizon: data.scenario?.planning_horizon,
          variant: data.scenario?.variant,
          totalDemand: data.demand_breakdown.total_demand_mw,
          dcShare: data.metrics?.dc_share_pct || 0,
          renewableTarget: data.metrics?.renewable_target_pct || 0,
          totalCapacity: totalCapacityMw,
        };
      });
  }, [pinned, pinnedAnalyses]);

  const queueSimulation = useCallback(async () => {
    if (!selectedScenario) {
      setSimulationError('Select a scenario before launching a simulation.');
      return;
    }

    setSimulationLoading(true);
    setSimulationError(null);
    try {
      const payload: Record<string, any> = {
        steps: simSteps,
        timestep_minutes: simTimestepMinutes,
        load_factor: simLoadFactor,
        base_demand_mw: baseDemandMw,
        base_dc_mw: baseDcMw,
        use_storage: simUseStorage,
        use_flex_loads: simUseFlexLoads,
      };
      if (selectedStrategy) {
        payload.strategy = selectedStrategy;
      }

      const response = await apiFetch(`/scenarios/${selectedScenario}/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await parseResponseJson<ScenarioSimulationResponse>(
        response,
        'Failed to queue scenario simulation'
      );
      setSimulation(data);
      setSimulationQueuedAt(new Date());
    } catch (e) {
      setSimulationError(e instanceof Error ? e.message : 'Failed to queue scenario simulation');
      setSimulation(null);
    } finally {
      setSimulationLoading(false);
    }
  }, [
    selectedScenario,
    simSteps,
    simTimestepMinutes,
    simLoadFactor,
    baseDemandMw,
    baseDcMw,
    simUseStorage,
    simUseFlexLoads,
    selectedStrategy,
  ]);

  return (
    <div className="h-full">
      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-4 h-full">
        <aside className="bg-gray-800 rounded-lg p-4 flex flex-col gap-4 min-h-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white">EDGS Scenarios</h2>
              <DataBadge tier="SCENARIO" />
            </div>
            <p className="text-xs text-gray-400">
              Explore MBIE EDGS scenarios with demand, supply, and fairness signals.
            </p>
            <DataProvenanceRow
              tier="SCENARIO"
              source="/api/v1/scenarios"
              asOf={listFetchedAt}
              refresh="on filter change"
              inputs={{
                variant: variantFilter || 'ALL',
                horizon: horizonFilter || 'ALL',
                base_demand_mw: baseDemandMw,
                base_dc_mw: baseDcMw,
              }}
              warnings={listError ? [listError] : null}
              className="mt-2"
              compact
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Variant</label>
              <select
                value={variantFilter}
                onChange={(e) => setVariantFilter(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
              >
                <option value="">All Variants</option>
                {variants.map((variant) => (
                  <option key={variant} value={variant}>{variant}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Horizon</label>
              <select
                value={horizonFilter}
                onChange={(e) => setHorizonFilter(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
              >
                <option value="">All Years</option>
                {horizons.map((horizon) => (
                  <option key={horizon} value={horizon}>{horizon}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name, variant..."
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Base Demand (MW)</label>
              <input
                type="number"
                value={baseDemandMw}
                min={1000}
                max={20000}
                step={50}
                onChange={(e) => setBaseDemandMw(Number(e.target.value))}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Base DC (MW)</label>
              <input
                type="number"
                value={baseDcMw}
                min={0}
                max={1000}
                step={10}
                onChange={(e) => setBaseDcMw(Number(e.target.value))}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
              />
            </div>
          </div>

          {listError && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg p-2 text-xs text-red-300">
              {listError}
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-2">
            {listLoading && (
              <div className="text-xs text-gray-400">Loading scenarios...</div>
            )}
            {!listLoading && filteredScenarios.length === 0 && (
              <div className="text-xs text-gray-500">No scenarios match the filters.</div>
            )}
            {!listLoading && filteredScenarios.map((scenario) => {
              const isPinned = pinned.includes(scenario.name);
              return (
                <button
                  key={scenario.name}
                  onClick={() => setSelectedScenario(scenario.name)}
                  className={`w-full text-left p-3 rounded-lg transition-colors border ${
                    selectedScenario === scenario.name
                      ? 'bg-blue-600/20 border-blue-500'
                      : 'bg-gray-700/50 border-transparent hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-white text-sm">{scenario.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${VARIANT_COLORS[scenario.variant] || 'bg-gray-500'} text-white`}>
                      {scenario.variant}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mb-2">
                    {scenario.planning_horizon} {scenario.description ? `- ${scenario.description.slice(0, 48)}` : ''}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{scenario.scenario_id}</span>
                    <span
                      onClick={(event) => {
                        event.stopPropagation();
                        togglePin(scenario.name);
                      }}
                      className={`px-2 py-0.5 rounded ${isPinned ? 'bg-yellow-600 text-gray-900' : 'bg-gray-600 text-gray-200'}`}
                    >
                      {isPinned ? 'Pinned' : 'Pin'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Comparison Tray</div>
            {compareError && (
              <div className="text-xs text-red-300 mb-2">{compareError}</div>
            )}
            {pinned.length === 0 && (
              <div className="text-xs text-gray-500">Pin up to three scenarios to compare.</div>
            )}
            {pinned.length > 0 && (
              <div className="space-y-1 text-xs text-gray-300">
                {pinned.map((name) => (
                  <div key={name} className="flex justify-between">
                    <span className="truncate">{name}</span>
                    <button
                      onClick={() => togglePin(name)}
                      className="text-gray-500 hover:text-gray-300"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        <section className="bg-gray-800 rounded-lg p-4 overflow-y-auto min-h-0">
          {!selectedScenario && (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <div className="text-lg font-medium text-gray-300">Select a scenario</div>
              <div className="text-sm text-gray-500">Pick a scenario to see demand, supply, and fairness signals.</div>
            </div>
          )}

          {analysisLoading && (
            <div className="text-sm text-gray-400">Loading scenario analysis...</div>
          )}

          {analysisError && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-sm text-red-300">
              {analysisError}
            </div>
          )}

          {analysis && !analysisLoading && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-2xl font-semibold text-white">{analysis.scenario.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${VARIANT_COLORS[analysis.scenario.variant] || 'bg-gray-600'} text-white`}>
                      {analysis.scenario.variant}
                    </span>
                    <span className="text-xs text-gray-400">{analysis.scenario.planning_horizon}</span>
                  </div>
                  <p className="text-sm text-gray-400 mt-1 max-w-2xl">
                    {analysis.scenario.description || 'EDGS scenario description unavailable.'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                    <span>Source: {analysis.scenario.source}</span>
                    <span>Scenario ID: {analysis.scenario.name}</span>
                  </div>
                </div>
                <button
                  onClick={() => togglePin(analysis.scenario.name)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    pinned.includes(analysis.scenario.name)
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  {pinned.includes(analysis.scenario.name) ? 'Pinned for Compare' : 'Pin to Compare'}
                </button>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-sm font-medium text-gray-300">Simulation Controls</div>
                    <DataBadge tier="MODELLED" />
                  </div>
                  <p className="text-xs text-gray-400">
                    Queue a chrono run with optional strategy presets and data-centre policy hooks.
                  </p>
                  <DataProvenanceRow
                    tier="MODELLED"
                    source={`/api/v1/scenarios/${analysis.scenario.name}/simulate`}
                    asOf={simulationQueuedAt}
                    refresh="manual"
                    inputs={{
                      scenario: analysis.scenario.name,
                      strategy: selectedStrategy || 'NONE',
                      steps: simSteps,
                      timestep_minutes: simTimestepMinutes,
                      load_factor: simLoadFactor,
                      base_demand_mw: baseDemandMw,
                      base_dc_mw: baseDcMw,
                    }}
                    warnings={simulationError ? [simulationError] : null}
                    className="mt-2"
                    compact
                  />
                  {strategyError && (
                    <div className="mt-2 text-xs text-red-300">{strategyError}</div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Strategy</label>
                      <select
                        value={selectedStrategy}
                        onChange={(e) => setSelectedStrategy(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm"
                        disabled={strategyLoading}
                      >
                        <option value="">No strategy</option>
                        {strategies.map((strategy) => (
                          <option key={strategy.name} value={strategy.name}>
                            {strategy.name}
                          </option>
                        ))}
                      </select>
                      {selectedStrategyMeta?.description && (
                        <div className="text-[11px] text-gray-500 mt-1">
                          {selectedStrategyMeta.description}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Steps</label>
                      <input
                        type="number"
                        min={1}
                        max={8760}
                        value={simSteps}
                        onChange={(e) => setSimSteps(Number(e.target.value))}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Timestep (min)</label>
                      <input
                        type="number"
                        min={1}
                        max={1440}
                        value={simTimestepMinutes}
                        onChange={(e) => setSimTimestepMinutes(Number(e.target.value))}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Load Factor</label>
                      <input
                        type="number"
                        min={0.1}
                        max={2.0}
                        step={0.05}
                        value={simLoadFactor}
                        onChange={(e) => setSimLoadFactor(Number(e.target.value))}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-300 mt-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={simUseStorage}
                        onChange={(e) => setSimUseStorage(e.target.checked)}
                        className="accent-blue-500"
                      />
                      Enable storage
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={simUseFlexLoads}
                        onChange={(e) => setSimUseFlexLoads(e.target.checked)}
                        className="accent-blue-500"
                      />
                      Enable flex loads
                    </label>
                  </div>
                  <button
                    onClick={queueSimulation}
                    disabled={simulationLoading || !selectedScenario}
                    className={`mt-3 w-full py-2 rounded-lg text-sm font-semibold ${
                      simulationLoading
                        ? 'bg-blue-900 text-blue-200 cursor-wait'
                        : 'bg-blue-600 text-white hover:bg-blue-500'
                    }`}
                  >
                    {simulationLoading ? 'Queueing Simulation...' : 'Queue Simulation'}
                  </button>
                </div>

                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-sm font-medium text-gray-300">Simulation Status</div>
                    <DataBadge tier="SYSTEM" />
                  </div>
                  {!simulation && (
                    <div className="text-xs text-gray-500">
                      Queue a simulation to track the task status and inputs.
                    </div>
                  )}
                  {simulation && (
                    <div className="space-y-2 text-xs text-gray-300">
                      <div>
                        Task ID: <span className="text-gray-100">{simulation.task_id}</span>
                      </div>
                      <div>Status: {simulation.status}</div>
                      <div>Strategy: {simulation.strategy || 'None'}</div>
                      <div>Steps: {simulation.config?.steps ?? simSteps}</div>
                      <div>Load Factor: {simulation.config?.load_factor ?? simLoadFactor}</div>
                    </div>
                  )}
                  {simulationError && (
                    <div className="mt-3 text-xs text-red-300">{simulationError}</div>
                  )}
                </div>
              </div>

              {comparisonRows.length >= 2 && (
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-300 mb-3">Comparison Snapshot</div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {comparisonRows.map((row) => (
                      <div key={row.name} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-white font-medium">{row.name}</span>
                          <span className="text-xs text-gray-400">{row.horizon} {row.variant}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-300">
                          <div>Total Demand: {row.totalDemand.toFixed(0)} MW</div>
                          <div>DC Share: {row.dcShare.toFixed(1)}%</div>
                          <div>Renewable Target: {row.renewableTarget.toFixed(0)}%</div>
                          <div>Capacity: {row.totalCapacity.toFixed(0)} MW</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricTile
                  label="Total Demand"
                  value={`${analysis.demand_breakdown.total_demand_mw.toFixed(0)} MW`}
                />
                <MetricTile
                  label="Demand Increase"
                  value={`+${analysis.demand_breakdown.demand_increase_pct.toFixed(1)}%`}
                  tone={analysis.demand_breakdown.demand_increase_pct > 30 ? 'warning' : 'good'}
                />
                <MetricTile
                  label="DC Demand"
                  value={`${analysis.demand_breakdown.dc_demand_mw.toFixed(0)} MW`}
                />
                <MetricTile
                  label="Renewable Target"
                  value={`${analysis.metrics.renewable_target_pct?.toFixed(0) || 0}%`}
                  tone="good"
                />
                <MetricTile
                  label="Total Capacity"
                  value={derived ? `${derived.totalCapacityMw.toFixed(0)} MW` : 'N/A'}
                />
                <MetricTile
                  label="Capacity Headroom"
                  value={derived ? `${derived.headroomMw.toFixed(0)} MW` : 'N/A'}
                  tone={derived && derived.headroomMw > 0 ? 'good' : 'warning'}
                />
                <MetricTile
                  label="Flex Potential"
                  value={derived ? `${derived.totalFlexMw.toFixed(0)} MW` : 'N/A'}
                />
                <MetricTile
                  label="DC Share"
                  value={`${analysis.metrics.dc_share_pct?.toFixed(1) || 0}%`}
                />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-300 mb-3">Demand Composition</div>
                  {[
                    { label: 'Base Load', value: analysis.demand_breakdown.scaled_base_mw, color: 'bg-gray-500' },
                    { label: 'EV', value: analysis.demand_breakdown.ev_demand_mw, color: 'bg-green-500' },
                    { label: 'Hydrogen', value: analysis.demand_breakdown.h2_demand_mw, color: 'bg-cyan-500' },
                    { label: 'Data Centres', value: analysis.demand_breakdown.dc_demand_mw, color: 'bg-pink-500' },
                    { label: 'Process Heat', value: analysis.demand_breakdown.process_heat_mw, color: 'bg-orange-500' },
                  ].map((item) => {
                    const pct = analysis.demand_breakdown.total_demand_mw
                      ? (item.value / analysis.demand_breakdown.total_demand_mw) * 100
                      : 0;
                    return (
                      <div key={item.label} className="mb-3">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{item.label}</span>
                          <span>{item.value.toFixed(0)} MW ({pct.toFixed(1)}%)</span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color}`} style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-300 mb-3">Generation Capacity Mix</div>
                  {(!derived || derived.totalCapacityGw === 0) && (
                    <div className="text-xs text-gray-500">No capacity data available.</div>
                  )}
                  {[
                    { label: 'Wind', value: analysis.scenario.generation_mix.wind_capacity_gw, color: 'bg-sky-500' },
                    { label: 'Solar', value: analysis.scenario.generation_mix.solar_capacity_gw, color: 'bg-yellow-400' },
                    { label: 'Hydro', value: analysis.scenario.generation_mix.hydro_capacity_gw, color: 'bg-blue-500' },
                    { label: 'Geothermal', value: analysis.scenario.generation_mix.geothermal_capacity_gw, color: 'bg-orange-500' },
                    { label: 'Gas', value: analysis.scenario.generation_mix.gas_capacity_gw, color: 'bg-red-500' },
                  ].map((item) => {
                    const totalGw = derived?.totalCapacityGw || 1;
                    const pct = totalGw > 0 ? (item.value / totalGw) * 100 : 0;
                    return (
                      <div key={item.label} className="mb-3">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{item.label}</span>
                          <span>{item.value.toFixed(2)} GW ({pct.toFixed(1)}%)</span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color}`} style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-300 mb-3">Flexibility & Storage</div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-300">
                    <div>
                      Battery Energy: {analysis.scenario.flexibility.battery_storage_gwh.toFixed(2)} GWh
                    </div>
                    <div>
                      Battery Power: {analysis.scenario.flexibility.battery_power_gw.toFixed(2)} GW
                    </div>
                    <div>
                      Pumped Hydro: {analysis.scenario.flexibility.pumped_hydro_gwh.toFixed(2)} GWh
                    </div>
                    <div>
                      Demand Response: {analysis.scenario.flexibility.demand_response_potential_mw.toFixed(0)} MW
                    </div>
                    <div>
                      DC Flex Share: {(analysis.scenario.flexibility.dc_flexibility_share * 100).toFixed(0)}%
                    </div>
                    <div>
                      H2 Flex Share: {(analysis.scenario.flexibility.h2_electrolyzer_flexibility * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-500">
                    Battery duration: {derived ? derived.batteryHours.toFixed(1) : 'N/A'} hours at max output.
                  </div>
                </div>

                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-300 mb-3">Regional Distribution</div>
                  {analysis.regional_loads.map((region) => {
                    const max = Math.max(...analysis.regional_loads.map((r) => r.total_modified_mw));
                    const pct = max ? (region.total_modified_mw / max) * 100 : 0;
                    const delta = region.total_modified_mw - region.total_base_mw;
                    return (
                      <div key={region.region} className="mb-3">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{REGION_LABELS[region.region] || region.region}</span>
                          <span>{region.total_modified_mw.toFixed(0)} MW (+{delta.toFixed(0)} MW)</span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                          <span>DC: {region.dc_load_mw.toFixed(0)} MW</span>
                          <span>EV: {region.ev_load_mw.toFixed(0)} MW</span>
                          <span>H2: {region.h2_load_mw.toFixed(0)} MW</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-300 mb-3">Fairness Context</div>
                  <div className="text-xs text-gray-300 space-y-2">
                    <div>
                      Price Sensitivity Factor: {analysis.scenario.fairness_context.price_sensitivity_factor.toFixed(2)}
                    </div>
                    <div>
                      Regional Constraints: {analysis.scenario.fairness_context.regional_constraints.length > 0
                        ? analysis.scenario.fairness_context.regional_constraints.join(', ')
                        : 'None listed'}
                    </div>
                    {analysis.scenario.fairness_context.notes && (
                      <div className="text-gray-400">
                        Notes: {analysis.scenario.fairness_context.notes}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-300 mb-3">Cost & Risk Signals</div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-300">
                    <div>Discount Rate: {(analysis.scenario.costs.discount_rate * 100).toFixed(1)}%</div>
                    <div>Gas Marginal Cost: {analysis.scenario.costs.marginal_cost_gas.toFixed(1)} $/MWh</div>
                    <div>Solar Marginal Cost: {analysis.scenario.costs.marginal_cost_solar.toFixed(3)} $/MWh</div>
                    <div>Wind Marginal Cost: {analysis.scenario.costs.marginal_cost_wind.toFixed(3)} $/MWh</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ScenariosView;
